import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { ZoomIn, ZoomOut, FitToScreen } from '@carbon/react/icons';
import type { SlotValuationInputsResponse } from 'types/packages-api/slot-valuation-inputs';
import { useModelMap } from './hooks';
import {
  initModelMapDiagram,
  type ModelMapDiagramHandle,
} from './model-map-diagram';
import { slotCategoryOf } from './utils';
import styles from './slot-tearsheet.module.scss';

/**
 * The model-map view of the tearsheet — the interactive registry diagram
 * (`POST /queries/model_map`) rendered full-width with GoJS, with zoom controls.
 * Mounted only when its tab is active — but the map REQUEST is not deferred with the mount:
 * the table view already needs the registry (units/labels), so `useModelMap` fires on open
 * whichever tab is showing (see useModelMap). Waits for the container to have real pixel
 * dimensions
 * before GoJS touches it (a hidden tab has none), then builds the diagram and
 * disposes it on unmount. A click highlights a node's upstream ancestry; a
 * background click resets it.
 *
 * ── THE TEARSHEET PAYLOAD IS AN INPUT, NOT JUST THE REGISTRY ─────────────────────────
 *
 * AC 4 and AC 6 are both SLOT-DEPENDENT — which discount rate applied, what value each
 * named input resolved to — so the diagram needs the same payload the table renders from.
 * It arrives as a PROP rather than through a second `useSlotValuationInputs` call: the
 * parent already holds it, and a second hook would be a second cache entry that could
 * disagree with the table's about the same slot.
 *
 * OPTIONAL, and every slot-dependent field fails open without it. The map is still the
 * registry graph when no slot is in scope.
 *
 * @param {object} props - The tearsheet payload, when one is loaded.
 * @return {JSX.Element} The diagram, or its loading / error state.
 */
export function TearsheetModelMap({
  response,
}: {
  response?: SlotValuationInputsResponse;
}) {
  const { data, isPending, isError, error } = useModelMap({ enabled: true });

  // AC 4 reads the slot's own category from the `slot_category` NODE — the same source the
  // table uses, through the same helper, so the two views cannot disagree about which of
  // the twelve base rates applied.
  const slotInputs = response?.slotInputs;
  const slotCategory = useMemo(
    () => (slotInputs ? slotCategoryOf(slotInputs) : null),
    [slotInputs]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<ModelMapDiagramHandle | null>(null);
  const [containerReady, setContainerReady] = useState(false);

  // The mock's model map is the diagram alone (no side panel), so selection has no
  // sink here — the click still drives the in-diagram highlight inside GoJS.
  const handleSelect = useCallback(() => {}, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerReady(true);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setContainerReady(true);
          observer.disconnect();
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerReady || !containerRef.current || !data) {
      return;
    }
    handleRef.current = initModelMapDiagram(
      containerRef.current,
      data,
      handleSelect,
      slotInputs ?? [],
      slotCategory,
      response?.domains ?? []
    );
    return () => {
      handleRef.current?.dispose();
      handleRef.current = null;
    };
  }, [
    containerReady,
    data,
    handleSelect,
    slotInputs,
    slotCategory,
    response?.domains,
  ]);

  if (isError) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Model map unavailable"
        subtitle={error?.message ?? 'The model map could not be loaded.'}
      />
    );
  }

  return (
    <div className={styles.mapCanvasWrap}>
      {isPending && (
        <div className={styles.mapLoading}>
          <InlineLoading description="Loading model map…" />
        </div>
      )}
      <div className={styles.mapToolbar}>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={ZoomIn}
          iconDescription="Zoom in"
          tooltipPosition="bottom"
          onClick={() => handleRef.current?.zoomIn()}
        />
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={ZoomOut}
          iconDescription="Zoom out"
          tooltipPosition="bottom"
          onClick={() => handleRef.current?.zoomOut()}
        />
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={FitToScreen}
          iconDescription="Fit to view"
          tooltipPosition="bottom"
          onClick={() => handleRef.current?.zoomToFit()}
        />
      </div>
      {/* GoJS paints into a <canvas> here, which is invisible to assistive tech. The Table
          view holds every value the map shows, so name this region and point there rather
          than leaving an unlabelled, unreachable graph (INS-11969 review). */}
      <div
        ref={containerRef}
        className={styles.mapCanvas}
        role="img"
        aria-label="Model map diagram. The same values are listed in the Table view."
      />
    </div>
  );
}
